import { useMemo, useState } from "react";
import { Shield, Search, Loader2, UserCog, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import TablePagination from "@/components/TablePagination";
import { useGameweeks } from "@/hooks/api/useGameweeks";
import {
  useAdminTeams,
  type AdminTeamFilters,
  type AdminTeamRow,
} from "@/hooks/api/useAdminTeams";
import AdminTeamDetailDrawer from "./AdminTeamDetailDrawer";
import { cn } from "@/lib/utils";

const STATUS_COLOR: Record<string, string> = {
  UPCOMING: "bg-gray-100 text-gray-700",
  OPEN: "bg-green-100 text-green-700",
  LOCKED: "bg-orange-100 text-orange-700",
  COMPLETED: "bg-blue-100 text-blue-700",
};

export default function AdminTeamsPage() {
  const [filters, setFilters] = useState<AdminTeamFilters>({
    page: 1,
    limit: 25,
  });
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  const { data: gameweeksData } = useGameweeks();
  const gameweeks = Array.isArray(gameweeksData) ? gameweeksData : [];

  const { data, isLoading, error } = useAdminTeams(filters);
  const teams: AdminTeamRow[] = data?.teams ?? [];
  const pagination = data?.pagination;

  // Sort gameweeks newest-first for the filter dropdown
  const sortedGameweeks = useMemo(
    () => [...gameweeks].sort((a, b) => b.id - a.id),
    [gameweeks],
  );

  const setSearch = (val: string) =>
    setFilters((f) => ({ ...f, search: val || undefined, page: 1 }));

  const setGameweek = (val: string) =>
    setFilters((f) => ({
      ...f,
      gameweekId: val === "all" ? undefined : Number(val),
      page: 1,
    }));

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-emerald-600" />
            Teams · Admin XI Editor
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Edit any user's XI for any matchweek. Per-gameweek scope — changes
            to MW2 don't affect MW1. <b>No deadline or rule guards apply.</b>{" "}
            Locked / completed gameweeks auto-recalc points after every change.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search user (name, email, username)…"
              value={filters.search || ""}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={
              filters.gameweekId == null ? "all" : String(filters.gameweekId)
            }
            onValueChange={setGameweek}
          >
            <SelectTrigger>
              <SelectValue placeholder="All matchweeks" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All matchweeks</SelectItem>
              {sortedGameweeks.map((gw) => (
                <SelectItem key={gw.id} value={String(gw.id)}>
                  {gw.name} · {gw.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(filters.limit ?? 25)}
            onValueChange={(val) =>
              setFilters((f) => ({ ...f, limit: Number(val), page: 1 }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 per page</SelectItem>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading teams…
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600 flex items-center justify-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Failed to load teams. Refresh or check the backend.
          </div>
        ) : teams.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Shield className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>No teams match these filters.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Matchweek</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Players</TableHead>
                <TableHead className="text-right">Total pts</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.map((t) => (
                <TableRow
                  key={t.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedTeamId(t.id)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {t.user.avatar ? (
                        <img
                          src={t.user.avatar}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {(t.user.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {t.user.name}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {t.user.username
                            ? `@${t.user.username}`
                            : t.user.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium text-gray-900">
                      {t.gameweek.name}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {t.gameweek.phase.replace(/_/g, " ")} · R
                      {t.gameweek.roundNumber}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      className={cn(
                        STATUS_COLOR[t.gameweek.status] ?? "bg-gray-100",
                        "border-0",
                      )}
                    >
                      {t.gameweek.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center text-sm text-gray-700">
                    {t.playerCount} / 11
                  </TableCell>
                  <TableCell className="text-right text-sm font-bold text-gray-900">
                    {t.totalPoints}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedTeamId(t.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                    >
                      <UserCog className="h-3.5 w-3.5" />
                      Edit XI
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {pagination && pagination.total > pagination.limit && (
        <div className="mt-4">
          <TablePagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            pageSize={pagination.limit}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
            onPageSizeChange={(limit) =>
              setFilters((f) => ({ ...f, limit, page: 1 }))
            }
          />
        </div>
      )}

      {/* Drawer — handles its own data fetch */}
      {selectedTeamId && (
        <AdminTeamDetailDrawer
          teamId={selectedTeamId}
          onClose={() => setSelectedTeamId(null)}
        />
      )}
    </Layout>
  );
}
